import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../utils/api';

const WASTE_TYPES = [
  { value: 'plastic',    label: 'Plastic',    factor: 2.1,  epr: true },
  { value: 'organic',    label: 'Organic',    factor: 0.58, epr: false },
  { value: 'electronic', label: 'Electronic', factor: 0.0,  epr: false },
  { value: 'metal',      label: 'Metal',      factor: 0.0,  epr: false },
  { value: 'hazardous',  label: 'Hazardous',  factor: 0.0,  epr: false },
  { value: 'mixed',      label: 'Mixed',      factor: 1.4,  epr: true },
];

const DISPOSAL_METHODS = [
  { value: 'landfill',        label: 'Landfill',         emits: true },
  { value: 'recycler',        label: 'Recycler',         emits: false },
  { value: 'composting',      label: 'Composting',       emits: false },
  { value: 'energy_recovery', label: 'Energy Recovery',  emits: false },
  { value: 'reuse',           label: 'Reuse',            emits: false },
];

const RECYCLERS = [
  { name: 'EcoRecycle India Pvt Ltd', materials: 'Plastic, Metal', cpcb: true, location: 'Mumbai, MH', contact: '#' },
  { name: 'GreenCycle Solutions',     materials: 'Electronic, Hazardous', cpcb: true, location: 'Bengaluru, KA', contact: '#' },
  { name: 'BioMass Composters',       materials: 'Organic, Mixed', cpcb: false, location: 'Pune, MH', contact: '#' },
  { name: 'RecycleStar',              materials: 'Plastic, Paper', cpcb: true, location: 'Chennai, TN', contact: '#' },
];

const PIE_COLORS = ['var(--neon-pink)', 'var(--neon-green)', 'var(--neon-cyan)', 'var(--neon-yellow)', 'var(--neon-orange)'];

export default function WasteManagement() {
  const [logs, setLogs]         = useState([]);
  const [epr, setEpr]           = useState(null);
  const [summary, setSummary]   = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [form, setForm]         = useState({ activity_type: 'plastic', quantity: '', disposal_method: 'landfill', recycler_name: '', period: '2025-Q4' });

  const selectedWaste    = WASTE_TYPES.find((w) => w.value === form.activity_type);
  const selectedDisposal = DISPOSAL_METHODS.find((d) => d.value === form.disposal_method);
  const co2eEstimate     = form.quantity && selectedWaste && selectedDisposal?.emits
    ? Math.round(parseFloat(form.quantity) * selectedWaste.factor / 10) / 100 : null;

  const load = () => {
    api.get('/services/logs?module=waste').then((r) => setLogs(r.data.logs || [])).catch(() => {});
    api.get('/services/epr').then((r) => setEpr(r.data)).catch(() => {});
    api.get('/services/summary').then((r) => setSummary(r.data.summary?.waste)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.quantity) { setError('Quantity is required.'); return; }
    setSubmitting(true);
    try {
      await api.post('/services/logs', {
        module: 'waste', activity_type: form.activity_type,
        quantity: parseFloat(form.quantity), unit: 'kg',
        disposal_method: form.disposal_method,
        notes: form.recycler_name ? `Recycler: ${form.recycler_name}` : '',
        period: form.period,
      });
      setSuccess('Waste entry logged. Circular economy metrics updated.');
      setForm((f) => ({ ...f, quantity: '', recycler_name: '' }));
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to log waste entry.');
    } finally { setSubmitting(false); }
  };

  // Build pie data from logs
  const disposalCounts = logs.reduce((acc, l) => {
    const k = l.disposal_method || 'landfill';
    acc[k] = (acc[k] || 0) + (l.quantity || 0);
    return acc;
  }, {});
  const pieData = Object.entries(disposalCounts).map(([name, value]) => ({ name, value: Math.round(value) }));

  const totalKg    = logs.reduce((s, l) => s + (l.quantity || 0), 0);
  const divertedKg = logs.filter((l) => l.disposal_method && l.disposal_method !== 'landfill').reduce((s, l) => s + (l.quantity || 0), 0);
  const diversionRate = totalKg > 0 ? Math.round((divertedKg / totalKg) * 100) : 0;

  const eprStatusColor = epr?.status === 'compliant' ? 'var(--neon-green)' : epr?.status === 'at_risk' ? 'var(--neon-yellow)' : 'var(--neon-pink)';

  return (
    <div className="page-shell page-stack">
      <section className="section-stack">
        <div className="section-tag">Green Services — Waste</div>
        <h1>Waste Management</h1>
        <p className="section-copy">Track, divert, and eliminate waste. Monitor circular economy rate and India CPCB EPR compliance.</p>
      </section>

      {/* Summary Stats */}
      <section className="stats-grid">
        <div className="surface-panel stat-card">
          <div className="stat-label">Total waste logged</div>
          <div className="stat-value" style={{ color: 'var(--neon-orange)' }}>
            {(totalKg / 1000).toFixed(2)} <span style={{ fontSize: '1rem' }}>t</span>
          </div>
          <div className="stat-sub">tonnes generated</div>
        </div>
        <div className="surface-panel stat-card">
          <div className="stat-label">Circular economy rate</div>
          <div className="stat-value" style={{ color: diversionRate >= 80 ? 'var(--neon-green)' : diversionRate >= 50 ? 'var(--neon-yellow)' : 'var(--neon-pink)' }}>
            {diversionRate}<span style={{ fontSize: '1rem' }}>%</span>
          </div>
          <div className="stat-sub">diverted from landfill</div>
        </div>
        <div className="surface-panel stat-card">
          <div className="stat-label">CO2e from waste</div>
          <div className="stat-value" style={{ color: 'var(--neon-orange)' }}>
            {((summary?.total_co2e_emitted_kg || 0) / 1000).toFixed(2)} <span style={{ fontSize: '1rem' }}>tCO2e</span>
          </div>
          <div className="stat-sub">Scope 1 — landfill emissions</div>
        </div>
        <div className="surface-panel stat-card">
          <div className="stat-label">EPR status</div>
          <div className="stat-value" style={{ color: eprStatusColor, fontSize: '1.2rem', textTransform: 'uppercase' }}>
            {epr?.status?.replace('_', ' ') || '—'}
          </div>
          <div className="stat-sub">CPCB EPR compliance</div>
        </div>
      </section>

      {/* EPR Status Banner */}
      {epr && (
        <section className="surface-panel glow-surface" style={{ padding: '1.25rem 1.5rem', borderLeft: `3px solid ${eprStatusColor}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '1.5rem', alignItems: 'center' }}>
            {[
              { label: 'Plastic consumed', value: `${(epr.plastic_consumed_kg || 0).toLocaleString('en-IN')} kg` },
              { label: 'EPR credits required', value: `${(epr.required_credits_kg || 0).toLocaleString('en-IN')} kg` },
              { label: 'Shortfall', value: `${(epr.shortfall_kg || 0).toLocaleString('en-IN')} kg`, color: epr.shortfall_kg > 0 ? 'var(--neon-pink)' : 'var(--neon-green)' },
            ].map((s) => (
              <div key={s.label}>
                <div className="stat-label">{s.label}</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.88rem', color: s.color || 'var(--text)', marginTop: '0.2rem' }}>{s.value}</div>
              </div>
            ))}
            <Link to="/services/packaging" className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }}>Packaging EPR →</Link>
          </div>
        </section>
      )}

      {/* Log Form */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1rem' }}>Log waste audit entry</div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label className="form-label">Waste type *</label>
              <select className="form-input" value={form.activity_type} onChange={(e) => setForm((f) => ({ ...f, activity_type: e.target.value }))}>
                {WASTE_TYPES.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Quantity (kg) *</label>
              <input className="form-input" type="number" min="0" step="any" placeholder="e.g. 250" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Disposal method *</label>
              <select className="form-input" value={form.disposal_method} onChange={(e) => setForm((f) => ({ ...f, disposal_method: e.target.value }))}>
                {DISPOSAL_METHODS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Recycler name (if applicable)</label>
              <input className="form-input" type="text" placeholder="e.g. EcoRecycle India" value={form.recycler_name} onChange={(e) => setForm((f) => ({ ...f, recycler_name: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Period</label>
              <select className="form-input" value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))}>
                {['2025-Q3', '2025-Q4', '2026-Q1', '2026-Q2'].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {(co2eEstimate !== null || selectedWaste) && (
            <div style={{ padding: '0.75rem 1rem', background: 'rgba(255,100,50,0.07)', borderRadius: '8px', border: '1px solid rgba(255,100,50,0.2)', marginBottom: '1rem', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              {selectedDisposal?.emits
                ? <div><div className="stat-label">Est. CO2e this entry</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.9rem', color: 'var(--neon-orange)' }}>{co2eEstimate ?? '—'} tCO2e</div></div>
                : <div><div className="stat-label">Scope 1 emissions</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.9rem', color: 'var(--neon-green)' }}>Zero (diverted)</div></div>
              }
              <div><div className="stat-label">EPR-liable</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem', color: selectedWaste?.epr ? 'var(--neon-yellow)' : 'var(--muted2)' }}>{selectedWaste?.epr ? 'Yes — CPCB EPR required' : 'No'}</div></div>
            </div>
          )}

          {error && <div style={{ color: 'var(--neon-pink)', fontFamily: 'Space Mono', fontSize: '0.72rem', marginBottom: '0.5rem' }}>{error}</div>}
          {success && <div style={{ color: 'var(--neon-green)', fontFamily: 'Space Mono', fontSize: '0.72rem', marginBottom: '0.5rem' }}>{success}</div>}
          <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Logging…' : 'Log waste entry'}</button>
        </form>
      </section>

      {/* Circular Economy Chart + Metrics */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1.25rem' }}>Circular economy metrics</div>
        <div style={{ display: 'grid', gridTemplateColumns: pieData.length > 0 ? '240px 1fr' : '1fr', gap: '2rem', alignItems: 'center' }}>
          {pieData.length > 0 && (
            <ResponsiveContainer width={220} height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: '0.62rem', fill: 'var(--muted2)' }}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `${v.toLocaleString('en-IN')} kg`} contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.72rem' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div>
              <div className="stat-label" style={{ marginBottom: '0.4rem' }}>Landfill diversion rate — target: &gt;80%</div>
              <div style={{ height: '8px', borderRadius: '4px', background: 'var(--border)', overflow: 'hidden', marginBottom: '0.3rem' }}>
                <div style={{ height: '100%', width: `${diversionRate}%`, background: diversionRate >= 80 ? 'var(--neon-green)' : diversionRate >= 50 ? 'var(--neon-yellow)' : 'var(--neon-pink)', borderRadius: '4px', transition: 'width 0.6s ease' }} />
              </div>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.75rem', color: 'var(--muted2)' }}>{diversionRate}% diverted — {divertedKg.toLocaleString('en-IN')} kg of {totalKg.toLocaleString('en-IN')} kg total</div>
            </div>
            {[
              { label: 'Recycled', value: `${disposalCounts['recycler'] ? disposalCounts['recycler'].toLocaleString('en-IN') : 0} kg`, color: 'var(--neon-green)' },
              { label: 'Composted', value: `${disposalCounts['composting'] ? disposalCounts['composting'].toLocaleString('en-IN') : 0} kg`, color: 'var(--neon-cyan)' },
              { label: 'Energy Recovery', value: `${disposalCounts['energy_recovery'] ? disposalCounts['energy_recovery'].toLocaleString('en-IN') : 0} kg`, color: 'var(--neon-yellow)' },
              { label: 'Landfilled', value: `${disposalCounts['landfill'] ? disposalCounts['landfill'].toLocaleString('en-IN') : 0} kg`, color: 'var(--neon-pink)' },
            ].map((m) => (
              <div key={m.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="stat-label">{m.label}</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem', color: m.color }}>{m.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recycler Directory */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1rem' }}>Verified recycler directory</div>
        <table className="data-table">
          <thead>
            <tr><th>Recycler</th><th>Materials accepted</th><th>CPCB registered</th><th>Location</th><th></th></tr>
          </thead>
          <tbody>
            {RECYCLERS.map((r) => (
              <tr key={r.name}>
                <td style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600, fontSize: '0.85rem' }}>{r.name}</td>
                <td style={{ fontSize: '0.78rem', color: 'var(--muted2)' }}>{r.materials}</td>
                <td>
                  <span className="badge" style={{ color: r.cpcb ? 'var(--neon-green)' : 'var(--muted2)', borderColor: r.cpcb ? 'var(--neon-green)' : 'var(--border)', fontSize: '0.6rem' }}>
                    {r.cpcb ? '✓ CPCB Registered' : 'Not registered'}
                  </span>
                </td>
                <td style={{ fontSize: '0.78rem', color: 'var(--muted2)' }}>{r.location}</td>
                <td>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '0.65rem' }}>Contact</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Activity Log */}
      {logs.length > 0 && (
        <section className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-tag" style={{ marginBottom: '1rem' }}>Waste audit log</div>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Waste type</th><th>Quantity (kg)</th><th>Disposal method</th><th>CO2e (tCO2e)</th></tr></thead>
            <tbody>
              {logs.slice(0, 10).map((l) => (
                <tr key={l.id}>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--muted2)' }}>{new Date(l.created_at).toLocaleDateString('en-IN')}</td>
                  <td style={{ fontSize: '0.85rem', textTransform: 'capitalize' }}>{WASTE_TYPES.find((w) => w.value === l.activity_type)?.label || l.activity_type}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem' }}>{l.quantity?.toLocaleString('en-IN')}</td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--muted2)', textTransform: 'capitalize' }}>{(l.disposal_method || '—').replace('_', ' ')}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', color: 'var(--neon-orange)' }}>{(l.co2e_emitted_kg / 1000).toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
